import { UpTransition } from '@components/animations'
import ConfirmDialog from '@components/dialogs/ConfirmDialog'
import DoctorDetails from '@components/doctrs/DoctorDetails'
import DoctorForm from '@components/forms/DoctorForm'
import DoctorToolbar from '@components/tables/Toolbars/DoctorToolbar'
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks'
import {
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
} from '@mui/material'
import {
  DataGridPro,
  GridColumns,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro'
import { DoctorDto } from '@services/api'
import {
  doFindDoctors,
  doRemoveDoctor,
  setDoctorFilters,
} from '@state/reducers/doctorReducer'
import { EditIcon, Eye, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'

type Props = {
  searchMode?: boolean
  onSelectionChange?: (doctors: DoctorDto[]) => void
  selectedRows?: DoctorDto[]
}

const DoctorList: React.FC<Props> = ({
  searchMode,
  onSelectionChange,
  selectedRows = [],
}) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const [isAddOpen, setIsAddOpen] = useState(false)
  const [editingDoctor, seteditingDoctor] = useState<DoctorDto | null>(null)
  const [selectedDoctor, setSelectedDoctor] = useState<DoctorDto | null>(null)
  const [deletingDoctor, setDeletingDoctor] = useState<DoctorDto | null>(null)
  const [isLoading, setisLoading] = useState(false)

  const { doctors, filters } = useAppSelector(({ doctor }) => ({
    doctors: doctor.doctors,
    filters: doctor.filters,
  }))

  const isCheckboxSelection = !!onSelectionChange

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'firstName',
        headerName: intl.formatMessage({ id: 'firstName' }),
        width: 150,
        renderCell: (params) => {
          return (
            <div className="flex items-center capitalize">
              {params.row.firstName}
            </div>
          )
        },
      },
      {
        field: 'lastName',
        headerName: intl.formatMessage({ id: 'lastName' }),
        width: 150,
        renderCell: (params) => {
          return (
            <div className="flex items-center uppercase">
              {params.row.lastName}
            </div>
          )
        },
      },
      {
        field: 'rppsNumber',
        headerName: intl.formatMessage({ id: 'rppsNumber' }),
        width: 150,
      },
      {
        field: 'email',
        headerName: intl.formatMessage({ id: 'email' }),
        width: 250,
      },
      {
        field: 'phoneNumber',
        headerName: intl.formatMessage({ id: 'phoneNumber' }),
        width: 200,
      },
      {
        field: 'skill',
        headerName: 'Spécialité',
        width: 200,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        align: 'right',
        width: 150,
        renderCell: (params) => (
          <div>
            <IconButton
              type="button"
              size="small"
              color="info"
              onClick={() => {
                setSelectedDoctor(params.row)
              }}
            >
              <Eye />
            </IconButton>
            {!searchMode && (
              <>
                <IconButton
                  size="small"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    seteditingDoctor(params.row)
                    setIsAddOpen(true)
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="error"
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    setDeletingDoctor(params.row)
                  }}
                >
                  <Trash2 />
                </IconButton>
              </>
            )}
          </div>
        ),
      },
    ],
    [intl],
  )

  const handleDeleteDoctor = () => {
    if (deletingDoctor) {
      dispatch(doRemoveDoctor(deletingDoctor?.id))
        .unwrap()
        .then(() => {
          setDeletingDoctor(null)
        })
    }
  }

  useEffect(() => {
    dispatch(setDoctorFilters({ page: 1, search: '' }))
  }, [])

  useEffect(() => {
    setisLoading(true)
    dispatch(doFindDoctors()).finally(() => {
      setisLoading(false)
    })
  }, [filters])

  const handleCloseForm = () => {
    setIsAddOpen(false)
    if (editingDoctor) {
      seteditingDoctor(null)
    }
  }

  return (
    <div className="h-full flex flex-col gap-4">
      <DoctorToolbar
        onAddClick={() => setIsAddOpen(true)}
        hideActions={searchMode || false}
      />
      <DataGridPro
        rows={doctors.datas}
        pagination
        disableMultipleSelection
        paginationMode="server"
        onPageChange={(page) => {
          dispatch(setDoctorFilters({ page: page + 1 }))
        }}
        pinnedColumns={{
          right: ['actions'],
        }}
        columns={
          isCheckboxSelection
            ? [
                {
                  field: 'selection',
                  headerName: '',
                  width: 50,
                  sortable: false,
                  renderCell: (
                    params: GridRenderCellParams<string, DoctorDto>,
                  ) => (
                    <Checkbox
                      checked={
                        !!selectedRows.find((doc) => doc.id === params.row.id)
                      }
                      onChange={(e) => {
                        const newSelection = e.target.checked
                          ? [...selectedRows, params.row]
                          : selectedRows.filter(
                              (doc) => doc.id !== params.row.id,
                            )
                        onSelectionChange?.(newSelection)
                      }}
                    />
                  ),
                },
                ...columns,
              ]
            : columns
        }
        loading={isLoading}
        components={{
          LoadingOverlay: LinearProgress,
        }}
        componentsProps={{
          toolbar: {
            onAddClick: () => setIsAddOpen(true),
            hideActions: searchMode,
          },
        }}
      />
      <Dialog
        fullWidth
        maxWidth="lg"
        open={isAddOpen}
        // onClose={handleCloseForm}
        TransitionComponent={UpTransition}
        disableEscapeKeyDown
      >
        <DialogContent>
          <DoctorForm editingDoctor={editingDoctor} onClose={handleCloseForm} />
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth
        open={selectedDoctor !== null}
        TransitionComponent={UpTransition}
        maxWidth="lg"
      >
        <DialogContent>
          {selectedDoctor && (
            <DoctorDetails
              onClose={() => {
                setSelectedDoctor(null)
              }}
              doctor={selectedDoctor}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!deletingDoctor}
        onConfirm={handleDeleteDoctor}
        severity="error"
        onCancel={() => setDeletingDoctor(null)}
        title="Suppression"
        message={`Supprimer le docteur ${deletingDoctor?.firstName} ${deletingDoctor?.lastName}`}
      />
    </div>
  )
}
export default DoctorList
